import { useEffect, useState } from "react";
import { PartyPopper, Send, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../i18n";
import useReveal from "../hooks/useReveal";
import { sendOwnerEmail } from "../lib/sendOwnerEmail";

const isEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

export default function EventsPage() {
  useReveal();
  const { toast } = useToast();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "", message: "", honey: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Events & Catering – Quattro Tavoli";
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !isEmail(form.email) || !form.phone) {
      toast({ title: t("events.toast.fillTitle"), description: t("events.toast.fillDesc") });
      return;
    }
    setSubmitting(true);
    try {
      await sendOwnerEmail({
        subject: `Event-Anfrage – ${form.name}${form.guests ? ` · ${form.guests} Gäste` : ""}`,
        replyTo: form.email,
        honey: form.honey,
        autoresponse: t("mail.autoEvent"),
        fields: {
          Typ: "Event / Catering",
          Name: form.name,
          email: form.email,
          Telefon: form.phone,
          Gäste: form.guests || "—",
          Nachricht: form.message || "—",
        },
      });
      setSent(true);
    } catch (err) {
      toast({ title: t("events.toast.errTitle"), description: t("events.toast.errDesc") });
    }
    setSubmitting(false);
  };

  const inputCls =
    "w-full rounded-xl border border-[#E7DAC6] bg-[#FAF4EA] px-4 py-3 outline-none focus:border-[#9C3B2E] focus:ring-2 focus:ring-[#9C3B2E]/20 transition";

  return (
    <main className="bg-[#FAF4EA]" data-testid="events-page">
      <Navbar solid />

      <section className="pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div className="reveal">
              <span className="inline-flex items-center gap-2 text-[#9C3B2E] text-xs uppercase tracking-[0.35em] mb-5">
                <PartyPopper className="w-4 h-4" /> {t("eventspage.eyebrow")}
              </span>
              <h1 className="font-display text-4xl sm:text-5xl font-700 text-[#2A2420] leading-tight">
                {t("eventspage.title1")} <span className="italic text-[#9C3B2E]">{t("eventspage.title2")}</span>
              </h1>

              <p className="mt-7 text-lg text-[#4A423B] leading-relaxed font-body">
                {t("eventspage.intro")}
              </p>

              <div className="mt-8 space-y-4 text-[#4A423B] font-body">
                <p>
                  <span className="font-600 text-[#2A2420]">{t("eventspage.hoursLabel")}: </span>
                  {t("eventspage.hoursVal")}
                </p>
                <p>
                  <span className="font-600 text-[#2A2420]">{t("eventspage.guestsLabel")}: </span>
                  {t("eventspage.guestsVal")}
                </p>
              </div>

              <div className="mt-8">
                <h3 className="font-display text-xl font-600 text-[#2A2420]">{t("eventspage.formulasTitle")}</h3>
                <ul className="mt-3 space-y-2 text-[#4A423B] font-body">
                  <li className="flex gap-2"><span className="text-[#9C3B2E]">•</span> {t("eventspage.formula1")}</li>
                  <li className="flex gap-2"><span className="text-[#9C3B2E]">•</span> {t("eventspage.formula2")}</li>
                </ul>
              </div>

              <div className="mt-8">
                <h3 className="font-display text-xl font-600 text-[#2A2420]">{t("eventspage.cateringTitle")}</h3>
                <p className="mt-3 text-[#6b6058] leading-relaxed font-body">
                  {t("eventspage.cateringText")}
                </p>
              </div>
            </div>

            <div className="reveal lg:sticky lg:top-28">
              {sent ? (
                <div
                  data-testid="events-success-panel"
                  className="rounded-3xl bg-white p-10 text-center shadow-[0_20px_50px_-25px_rgba(42,36,32,0.35)] border border-[#EFE4D2]"
                >
                  <div className="mx-auto w-16 h-16 grid place-items-center rounded-full bg-[#9C3B2E]/10">
                    <CheckCircle2 className="w-9 h-9 text-[#9C3B2E]" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-600 text-[#2A2420]">
                    {t("eventspage.success.title")}
                  </h2>
                  <p className="mt-3 text-[#6b6058] leading-relaxed font-body">
                    {t("eventspage.success.text")}
                  </p>
                  <button
                    type="button"
                    data-testid="events-send-another-button"
                    onClick={() => {
                      setForm({ name: "", email: "", phone: "", guests: "", message: "", honey: "" });
                      setSent(false);
                    }}
                    className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#9C3B2E] text-[#9C3B2E] font-500 tracking-wide hover:bg-[#9C3B2E] hover:text-[#FAF4EA] transition-all duration-300"
                  >
                    {t("eventspage.success.again")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="events-form"
                  className="rounded-3xl bg-white p-8 shadow-[0_20px_50px_-25px_rgba(42,36,32,0.35)] border border-[#EFE4D2]"
                >
                  <h2 className="font-display text-2xl font-600 text-[#2A2420]">{t("eventspage.formTitle")}</h2>
                  <p className="mt-2 text-sm text-[#6b6058]">{t("events.form.sub")}</p>

                  <div className="mt-6 space-y-5">
                    <div>
                      <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("events.form.name")}</label>
                      <input
                        data-testid="events-name-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={inputCls}
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("events.form.email")}</label>
                        <input
                          data-testid="events-email-input"
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className={inputCls}
                          placeholder="mario@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("events.form.phone")}</label>
                        <input
                          data-testid="events-phone-input"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className={inputCls}
                          placeholder="+49 ..."
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("eventspage.guestsField")}</label>
                      <input
                        data-testid="events-guests-input"
                        type="number"
                        min="1"
                        value={form.guests}
                        onChange={(e) => setForm({ ...form, guests: e.target.value })}
                        className={inputCls}
                        placeholder={t("eventspage.guestsPh")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-500 text-[#4A423B] mb-2">{t("events.form.message")}</label>
                      <textarea
                        data-testid="events-message-input"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={5}
                        className={`${inputCls} resize-none`}
                        placeholder={t("events.form.msgPh")}
                      />
                    </div>
                  </div>

                  <input
                    type="text"
                    name="_honey"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.honey}
                    onChange={(e) => setForm({ ...form, honey: e.target.value })}
                    className="absolute -left-[9999px] w-px h-px opacity-0"
                  />

                  <button
                    type="submit"
                    data-testid="events-submit-button"
                    disabled={submitting}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#9C3B2E] text-[#FAF4EA] font-500 tracking-wide hover:bg-[#9C3B2E] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-10px_rgba(181,86,58,0.7)] disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {submitting ? t("events.form.sending") : t("events.form.submit")} <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}